import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Plus, CheckCircle, XCircle, Ban, BookOpen,
  AlertTriangle, FileSignature, Download, Clock, BellRing, GraduationCap,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { StatusBadge } from '../components/UI/StatusBadge';
import FormSelect from '../components/UI/FormSelect';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Modal from '../components/UI/Modal';
import ConfirmModal from '../components/UI/ConfirmModal';
import SelfServiceListSkeleton from '../components/UI/SelfServiceListSkeleton';
import PillToggle from '../components/UI/PillToggle';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import TradeBadge from '../components/UI/TradeBadge';
import SignatureCanvas from '../components/AttendanceEvents/SignatureCanvas';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';
import {
  useCourseRequestCapabilities,
  useMyCourseRequests,
  useCourseRequestPendingHod,
  useNominatableCourseWorkers,
  useMasterCourses,
  useCreateCourseRequest,
  useCourseRequestDecision,
  useCourseRequestDetail,
  useAcknowledgeSponsorshipAgreement,
} from '../hooks/useCourseRequests';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/numberUtils';
import {
  COURSE_REQUEST_PURPOSE_OPTIONS,
  COURSE_REQUEST_PURPOSE_OTHER,
} from '../constants/courseRequestPurpose';
import {
  formatPreferredDays,
  formatPreferredSessions,
  courseOffersLanguagePreference,
} from '../constants/courseRequestSchedule';
import {
  formatProjectLabel,
  formatStoredProjectLabel,
} from '../utils/projectDisplayUtils';

const COURSE_SOURCE_OPTIONS = [
  { id: 'library', label: 'Course Library' },
  { id: 'other', label: 'Not in library' },
];

const emptyForm = () => ({
  applicant_staff_id: '',
  master_course_code: '',
  intent_course_name: '',
  purpose_code: '',
  purpose_notes: '',
  purpose_source: '',
  project_code: '',
  project_site: '',
  preferred_language: '',
  use_library: true,
});

const SelfServiceCourseRequests = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const isCompact = useIsMobile(1024);
  const worklistRef = useRef(null);
  const detailPanelRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const restoreListScrollRef = useRef(false);

  const { data: caps, isLoading: capsLoading } = useCourseRequestCapabilities();
  const { data: myRequests = [], isLoading: myLoading } = useMyCourseRequests();
  const { data: pendingHod = [], isLoading: hodLoading } = useCourseRequestPendingHod({
    enabled: !!caps?.is_hod || !!caps?.is_hr,
  });
  const { data: workers = [] } = useNominatableCourseWorkers({
    enabled: !!caps?.can_nominate_workers,
  });
  const { data: masterCourses = [], isFetched: masterCoursesFetched } = useMasterCourses();
  const { data: detail } = useCourseRequestDetail(selectedId, { enabled: !!selectedId });

  const createMutation = useCreateCourseRequest();
  const decisionMutation = useCourseRequestDecision();
  const acknowledgeMutation = useAcknowledgeSponsorshipAgreement();

  const [tab, setTab] = useState('my');
  const [showNew, setShowNew] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [comments, setComments] = useState('');
  const [showSignAgreement, setShowSignAgreement] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);
  const signatureRef = useRef(null);
  // One-shot handoff from Cert Compliance (?nominate=1&applicant=&master_course=).
  const nominateAppliedRef = useRef(false);
  const [complianceHandoff, setComplianceHandoff] = useState(null);

  const { data: activeProjects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: async () => {
      const response = await staffAPI.getActiveProjects();
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: showNew,
  });

  const selectedWorker = useMemo(
    () => (workers || []).find((w) => w.staff_id === form.applicant_staff_id) || null,
    [workers, form.applicant_staff_id],
  );

  const selectedCourse = useMemo(
    () => (masterCourses || []).find((course) => course.code === form.master_course_code) || null,
    [masterCourses, form.master_course_code],
  );

  const showLanguagePicker = form.use_library && courseOffersLanguagePreference(
    form.master_course_code,
    caps?.language_preference_course_codes,
  );

  const selectedProject = useMemo(
    () => (activeProjects || []).find((p) => p.project_code === form.project_code) || null,
    [activeProjects, form.project_code],
  );

  const projectDisplayValue = useMemo(() => {
    if (!form.project_code) return '';
    if (selectedProject) {
      return formatProjectLabel(selectedProject.project_name, selectedProject.project_code);
    }
    return formatStoredProjectLabel(form.project_site || form.project_code);
  }, [form.project_code, form.project_site, selectedProject]);

  const list = tab === 'hod' ? pendingHod : myRequests;
  const loading = capsLoading || myLoading || (tab === 'hod' && hodLoading);

  const openDetail = (id) => {
    if (id && isCompact) {
      const scrollContainer = worklistRef.current?.closest('main');
      savedScrollTopRef.current = scrollContainer?.scrollTop || 0;
      restoreListScrollRef.current = false;
    }
    setSearchParams(id ? { id } : {});
    setComments('');
  };

  const closeCompactDetail = () => {
    restoreListScrollRef.current = true;
    setSearchParams({});
    setComments('');
  };

  useEffect(() => {
    if (!isCompact) return undefined;

    const frame = window.requestAnimationFrame(() => {
      if (selectedId) {
        detailPanelRef.current?.scrollIntoView({ block: 'start' });
        return;
      }
      if (restoreListScrollRef.current) {
        const scrollContainer = worklistRef.current?.closest('main');
        scrollContainer?.scrollTo({ top: savedScrollTopRef.current, behavior: 'auto' });
        restoreListScrollRef.current = false;
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isCompact, selectedId]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    if (selectedId) {
      setSearchParams({});
      setComments('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (caps?.can_nominate_workers && !caps?.can_self_apply && !form.applicant_staff_id) {
      return;
    }
    if (form.use_library && !form.master_course_code) return;
    if (!form.use_library && !(form.intent_course_name || '').trim()) return;
    if (!form.purpose_code) return;
    if (
      form.purpose_code === COURSE_REQUEST_PURPOSE_OTHER
      && (form.purpose_notes || '').trim().length < 10
    ) {
      return;
    }

    let projectSite = '';
    if (form.project_code && selectedProject) {
      projectSite = formatProjectLabel(selectedProject.project_name, selectedProject.project_code);
    } else if ((form.project_site || '').trim()) {
      projectSite = form.project_site.trim();
    }

    const payload = {
      purpose_code: form.purpose_code,
      purpose_notes: (form.purpose_notes || '').trim(),
      project_site: projectSite,
    };
    if (caps?.can_nominate_workers && form.applicant_staff_id) {
      payload.applicant_staff_id = form.applicant_staff_id;
    }
    if (form.use_library) {
      payload.master_course_code = form.master_course_code;
    } else {
      payload.intent_course_name = form.intent_course_name.trim();
    }
    if (showLanguagePicker && form.preferred_language) {
      payload.preferred_language = form.preferred_language;
    }
    await createMutation.mutateAsync(payload);
    setShowNew(false);
    setForm(emptyForm());
    setComplianceHandoff(null);
    setTab('my');
  };

  const closeNewRequest = () => {
    setShowNew(false);
    setForm(emptyForm());
    setComplianceHandoff(null);
  };

  // Cert Compliance → Nominate deep-link: open New Request with worker + course.
  useEffect(() => {
    if (nominateAppliedRef.current) return;
    if (searchParams.get('nominate') !== '1') return;
    if (capsLoading || !caps) return;

    const masterCourse = (searchParams.get('master_course') || '').trim();
    // Wait for library so purpose suggestion + course name resolve correctly.
    if (masterCourse && !masterCoursesFetched) return;

    nominateAppliedRef.current = true;

    const stripNominateParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('nominate');
      next.delete('applicant');
      next.delete('master_course');
      setSearchParams(next, { replace: true });
    };

    if (!caps.can_request) {
      toast.error('You do not have permission to create course requests');
      stripNominateParams();
      return;
    }

    const applicant = (searchParams.get('applicant') || '').trim();
    const course = (masterCourses || []).find((item) => item.code === masterCourse) || null;

    if (applicant && caps.can_nominate_workers === false) {
      toast.error('You can only create course requests for yourself');
      stripNominateParams();
      return;
    }

    if (masterCourse && !course) {
      toast.warning(
        `Course ${masterCourse} was not found in the library. Pick a course manually.`,
      );
    }

    const suggestion = course?.suggested_request_purpose_code || (
      masterCourse ? 'legal_requirement' : ''
    );
    setForm({
      ...emptyForm(),
      applicant_staff_id: caps.can_nominate_workers ? applicant : '',
      master_course_code: course ? masterCourse : '',
      use_library: true,
      purpose_code: suggestion,
      purpose_source: suggestion ? 'suggested' : '',
    });
    setComplianceHandoff({
      applicantStaffId: applicant,
      courseCode: masterCourse,
      courseName: course?.name || masterCourse,
    });
    setShowNew(true);
    stripNominateParams();
  }, [
    caps,
    capsLoading,
    masterCourses,
    masterCoursesFetched,
    searchParams,
    setSearchParams,
  ]);

  const act = async (action) => {
    if (!selectedId) return;
    await decisionMutation.mutateAsync({ id: selectedId, action, comments });
    setComments('');
  };

  const isMyAgreementToSign = (item) => (
    item?.agreement_status === 'pending_acknowledgment'
    && item?.applicant_staff_id === user?.staff_id
  );

  const openSignAgreement = () => {
    setDeclarationAccepted(false);
    setHasSignature(false);
    setShowSignAgreement(true);
  };

  const closeSignAgreement = () => {
    setShowSignAgreement(false);
    setDeclarationAccepted(false);
    setHasSignature(false);
    signatureRef.current?.clear?.();
  };

  const handleAcknowledgeAgreement = async (e) => {
    e.preventDefault();
    if (!selectedId || !declarationAccepted || !hasSignature) return;
    const signatureDataUrl = signatureRef.current?.toDataURL?.() || '';
    if (!signatureDataUrl) return;
    await acknowledgeMutation.mutateAsync({
      id: selectedId,
      data: {
        signature_data_url: signatureDataUrl,
        declaration_accepted: declarationAccepted,
      },
    });
    closeSignAgreement();
  };

  const handleDownloadAgreement = async (id) => {
    if (!id || downloadingAgreement) return;
    setDownloadingAgreement(true);
    try {
      const response = await staffAPI.downloadCourseAgreement(id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Sponsorship_Agreement_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error('Failed to download the sponsorship agreement');
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const canActHod = detail?.status === 'pending_hod' && (caps?.is_hod || caps?.is_hr);
  const canCancel = Boolean(detail?.can_cancel);
  const isNominatedAttendee = Boolean(
    detail
    && user?.staff_id
    && user.staff_id === detail.applicant_staff_id
    && user.staff_id !== detail.requested_by_staff_id
    && ['pending_hod', 'pending_hr', 'approved', 'booked'].includes(detail.status),
  );

  const hasUpcomingSession = useMemo(() => {
    if (!detail || detail.status !== 'booked') return false;
    const today = new Date().toISOString().slice(0, 10);
    if (detail.course_session_dates?.length) {
      return detail.course_session_dates.some((d) => d >= today);
    }
    return Boolean(detail.course_start_date && detail.course_start_date >= today);
  }, [detail]);

  const tabs = [
    { id: 'my', name: 'My requests' },
    ...((caps?.is_hod || caps?.is_hr)
      ? [{ id: 'hod', name: 'HOD approvals', count: pendingHod.length }]
      : []),
  ];

  if (loading && !myRequests.length) {
    return (
      <DashboardLayout>
        <SelfServiceListSkeleton />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Course Requests
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Request training and track each approval and booking step.
              </p>
            </div>
          </div>
        </div>

        {(!isCompact || !selectedId) && (
          <div className="px-1 flex items-center justify-between gap-3">
            <PillToggle
              options={tabs.map((t) => ({
                id: t.id,
                label: t.name,
                count: t.count,
              }))}
              value={tab}
              onChange={changeTab}
              theme="employee"
              variant="text"
              className="w-fit max-w-full min-w-0"
              ariaLabel="Course request view"
            />
            {caps?.can_request && (
              <button
                type="button"
                onClick={() => setShowNew(true)}
                className="btn btn-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
                title="New request"
                aria-label="New request"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Request</span>
              </button>
            )}
          </div>
        )}

        <div
          ref={worklistRef}
          className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6"
        >
          <div className={isCompact && selectedId ? 'hidden' : 'space-y-3'}>
            {list.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No course requests yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  {caps?.can_request
                    ? 'Submit a request to get started.'
                    : 'Ask your Trade Leader if you need training.'}
                </p>
              </div>
            ) : (
              list.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openDetail(item.id)}
                  className={`w-full text-left bg-white rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                    selectedId === item.id
                      ? 'border-blue-800 shadow-md'
                      : 'border-gray-200 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {item.display_course_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {item.request_number} · {item.applicant_name}
                        {item.trade_name ? ` · ${item.trade_name}` : ''}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusBadge status={item.status} />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{item.purpose}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-400">{formatDate(item.created_at)}</p>
                    {isMyAgreementToSign(item) && (
                      <StatusBadge status="pending" label="Sign agreement" showIcon />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          <div
            ref={detailPanelRef}
            className={isCompact && !selectedId ? 'hidden' : ''}
          >
            {isCompact && selectedId && (
              <button
                type="button"
                onClick={closeCompactDetail}
                className="btn btn-outline mb-3"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to requests
              </button>
            )}
            {!selectedId ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
                Select a request to view details
              </div>
            ) : !detail ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center">
                <LoadingSpinner size="md" />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-500">{detail.request_number}</p>
                    <h2 className="text-lg font-semibold text-gray-900 mt-0.5">
                      {detail.display_course_name}
                    </h2>
                  </div>
                  <StatusBadge status={detail.status} />
                </div>

                {isMyAgreementToSign(detail) && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-900">
                        HR issued the Sponsorship Agreement for this course. Review and sign it —
                        your course cannot be booked until you acknowledge it.
                      </p>
                      <button
                        type="button"
                        onClick={openSignAgreement}
                        className="btn btn-primary btn-sm mt-2 inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      >
                        <FileSignature className="w-3.5 h-3.5" /> Review &amp; sign agreement
                      </button>
                    </div>
                  </div>
                )}

                <dl className="grid grid-cols-1 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">Applicant</dt>
                    <dd className="font-medium text-gray-900">{detail.applicant_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Requested by</dt>
                    <dd className="text-gray-800">{detail.requested_by_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Purpose</dt>
                    <dd className="text-gray-800">
                      {detail.purpose_code ? (
                        <>
                          <span className="font-medium">
                            {detail.purpose_code_display}
                          </span>
                          {(detail.purpose_code === COURSE_REQUEST_PURPOSE_OTHER
                            ? detail.purpose
                            : detail.purpose_notes) ? (
                              <span className="block text-gray-600 mt-1 whitespace-pre-wrap">
                                {detail.purpose_code === COURSE_REQUEST_PURPOSE_OTHER
                                  ? detail.purpose
                                  : detail.purpose_notes}
                              </span>
                            ) : null}
                        </>
                      ) : (
                        <span className="whitespace-pre-wrap">{detail.purpose}</span>
                      )}
                    </dd>
                  </div>
                  {detail.project_site && (
                    <div>
                      <dt className="field-label">Project</dt>
                      <dd className="field-display mt-1">
                        {formatStoredProjectLabel(detail.project_site)}
                      </dd>
                    </div>
                  )}
                  {detail.preferred_days?.length > 0 && (
                    <div>
                      <dt className="field-label">Requested days</dt>
                      <dd className="field-display mt-1">{formatPreferredDays(detail.preferred_days)}</dd>
                    </div>
                  )}
                  {detail.preferred_timing?.length > 0 && (
                    <div>
                      <dt className="field-label">Requested sessions</dt>
                      <dd className="field-display mt-1">{formatPreferredSessions(detail.preferred_timing)}</dd>
                    </div>
                  )}
                  {detail.preferred_language && (
                    <div>
                      <dt className="field-label">Preferred language</dt>
                      <dd className="field-display mt-1">{detail.preferred_language_display}</dd>
                    </div>
                  )}
                  {detail.booked_course_name && (
                    <div>
                      <dt className="field-label">Confirmed course</dt>
                      <dd className="field-display-multiline mt-1">{detail.booked_course_name}</dd>
                    </div>
                  )}
                  {detail.course_schedule_pattern ? (
                    <div>
                      <dt className="field-label">Confirmed schedule</dt>
                      <dd className="field-display-multiline mt-1">
                        <span className="font-medium">{detail.course_schedule_display}</span>
                        {detail.course_session_dates?.length > 0 && (
                          <span className="block mt-1">
                            Sessions: {detail.course_session_dates.map((date) => formatDate(date)).join(', ')}
                          </span>
                        )}
                        {detail.course_schedule_notes && (
                          <span className="block mt-1 whitespace-pre-wrap text-gray-600">
                            {detail.course_schedule_notes}
                          </span>
                        )}
                      </dd>
                    </div>
                  ) : detail.course_start_date ? (
                    <div>
                      <dt className="field-label">Training period</dt>
                      <dd className="field-display mt-1">
                        {formatDate(detail.course_start_date)}
                        {detail.course_end_date
                          && detail.course_end_date !== detail.course_start_date
                          ? ` – ${formatDate(detail.course_end_date)}`
                          : ''}
                      </dd>
                    </div>
                  ) : null}
                  {detail.bond_required && (
                    <div>
                      <dt className="field-label">Sponsorship bond</dt>
                      <dd className="field-display mt-1">{detail.bond_duration_months} months</dd>
                    </div>
                  )}
                  {detail.agreement_status && detail.agreement_status !== 'not_required' && (
                    <div>
                      <dt className="field-label">Sponsorship agreement</dt>
                      <dd className="field-display mt-1">
                        {detail.agreement_status === 'acknowledged' ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge status="acknowledged" label="Signed" showIcon />
                            {detail.agreement_acknowledged_at && (
                              <span className="text-xs text-gray-500">
                                {formatDate(detail.agreement_acknowledged_at)}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDownloadAgreement(detail.id)}
                              disabled={downloadingAgreement}
                              className="inline-flex items-center gap-1 text-blue-800 hover:text-blue-900 text-xs font-medium disabled:opacity-50"
                            >
                              <Download className="w-3.5 h-3.5" /> Download PDF
                            </button>
                          </div>
                        ) : (
                          <StatusBadge status="pending" label="Awaiting your signature" showIcon />
                        )}
                      </dd>
                    </div>
                  )}
                  {detail.result && (
                    <div>
                      <dt className="text-xs text-gray-500">Result</dt>
                      <dd><StatusBadge status={detail.result} /></dd>
                    </div>
                  )}
                  {detail.rejection_reason && (
                    <div>
                      <dt className="text-xs text-gray-500">Rejection reason</dt>
                      <dd className="text-red-700">{detail.rejection_reason}</dd>
                    </div>
                  )}
                </dl>

                {isNominatedAttendee && (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                    <p className="text-sm text-blue-800 leading-relaxed">
                      {detail.requested_by_name
                        ? `${detail.requested_by_name} requested this course for you.`
                        : 'This course was requested for you.'}
                      {' '}
                      If you cannot attend, tell them. You cannot cancel the booking from here.
                    </p>
                  </div>
                )}

                {hasUpcomingSession && (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <BellRing className="w-4 h-4 text-blue-800" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-blue-900">
                          Session reminders
                        </h3>
                        <p className="mt-1 text-sm text-blue-800 leading-relaxed">
                          You will get a WhatsApp and in-app reminder at 4:00 PM
                          the day before each session, and again at 6:00 AM on
                          the morning of the course.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {(canActHod || canCancel) && (
                  <div className="border-t border-gray-100 pt-4 space-y-3">
                    {canActHod && (
                      <textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        rows={2}
                        placeholder="Comments / reason"
                        className="input w-full"
                      />
                    )}
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2">
                      {canCancel && (
                        <button
                          type="button"
                          disabled={decisionMutation.isPending}
                          onClick={() => setShowCancelConfirm(true)}
                          className="btn btn-secondary w-full sm:w-auto"
                        >
                          <Ban className="w-4 h-4" /> Cancel request
                        </button>
                      )}
                      {canActHod && (
                        <>
                          <button
                            type="button"
                            disabled={decisionMutation.isPending}
                            onClick={() => act('reject-hod')}
                            className="btn btn-danger w-full sm:w-auto inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          >
                            <XCircle className="w-4 h-4" /> Reject
                          </button>
                          <button
                            type="button"
                            disabled={decisionMutation.isPending}
                            onClick={() => act('approve-hod')}
                            className="btn btn-primary w-full sm:w-auto inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          >
                            <CheckCircle className="w-4 h-4" /> Approve
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showNew}
        onClose={closeNewRequest}
        title="New Course Request"
        size="lg"
        theme="employee"
        footer={(
          <>
            <button
              type="button"
              onClick={closeNewRequest}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-course-request-form"
              disabled={createMutation.isPending}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Submitting…' : 'Submit request'}
            </button>
          </>
        )}
      >
        <form id="new-course-request-form" onSubmit={handleSubmit} className="space-y-5">
          {complianceHandoff && (
            <div className="flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-3 text-sm text-blue-900">
              <GraduationCap className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
              <div className="min-w-0">
                <p className="font-medium">From Cert Compliance</p>
                <p className="text-blue-800/90 mt-0.5">
                  Prefilling
                  {complianceHandoff.applicantStaffId ? (
                    <>
                      {' '}
                      <span className="font-medium">{complianceHandoff.applicantStaffId}</span>
                    </>
                  ) : null}
                  {complianceHandoff.courseName ? (
                    <>
                      {' '}for{' '}
                      <span className="font-medium">{complianceHandoff.courseName}</span>
                      {complianceHandoff.courseCode ? (
                        <span className="text-blue-700/80"> ({complianceHandoff.courseCode})</span>
                      ) : null}
                    </>
                  ) : null}
                  . Review purpose and submit when ready.
                </p>
              </div>
            </div>
          )}

          {/* 1. Who */}
          {caps?.can_nominate_workers ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {caps?.can_self_apply ? 'Who is attending?' : 'Worker'}
              </label>
              <SearchableCombobox
                items={workers || []}
                value={form.applicant_staff_id}
                onChange={(staffId) => setForm((p) => ({ ...p, applicant_staff_id: staffId || '' }))}
                placeholder={caps?.can_self_apply ? 'Select worker, or leave blank for yourself' : 'Search worker…'}
                searchPlaceholder="Search name or staff ID…"
                emptyLabel="No matching workers"
                clearLabel={caps?.can_self_apply ? 'Myself' : undefined}
                variant="form"
                theme="employee"
                displayFn={(w) => [w.name, w.staff_id, w.trade_code].filter(Boolean).join(' · ')}
                valueFn={(w) => w.staff_id}
                displayValue={
                  selectedWorker
                    ? [
                      selectedWorker.name,
                      selectedWorker.staff_id,
                      selectedWorker.trade_code,
                    ].filter(Boolean).join(' · ')
                    : (form.applicant_staff_id || '')
                }
                searchFn={(w, term) => {
                  const q = term.toLowerCase();
                  return (
                    (w.name || '').toLowerCase().includes(q)
                    || (w.staff_id || '').toLowerCase().includes(q)
                    || (w.trade_code || '').toLowerCase().includes(q)
                  );
                }}
                renderItem={({ item, isSelected, isHighlighted, onSelect }) => (
                  <button
                    key={item.staff_id}
                    type="button"
                    onClick={onSelect}
                    className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50' : ''
                    } ${isHighlighted ? 'bg-gray-100' : ''}`}
                  >
                    <span className={`block truncate text-sm font-medium ${
                      isSelected ? 'text-blue-800' : 'text-gray-900'
                    }`}>
                      {item.name}
                    </span>
                    <span className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <span>{item.staff_id}</span>
                      {item.trade_code && (
                        <>
                          <span aria-hidden="true">·</span>
                          <TradeBadge
                            code={item.trade_code}
                            name={item.trade_name}
                            size="xs"
                          />
                        </>
                      )}
                    </span>
                  </button>
                )}
              />
              {caps?.can_self_apply && !form.applicant_staff_id && (
                <p className="mt-2 text-sm text-blue-800 bg-blue-50 rounded-xl px-3 py-2">
                  Applying for yourself · {user?.first_name} {user?.last_name}
                </p>
              )}
            </div>
          ) : caps?.can_self_apply ? (
            <div className="text-sm text-blue-800 bg-blue-50 rounded-xl px-3 py-2.5">
              Applying for yourself · {user?.first_name} {user?.last_name}
            </div>
          ) : null}

          {/* 2. What course */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Course</label>
              <PillToggle
                value={form.use_library ? 'library' : 'other'}
                onChange={(id) => setForm((previous) => {
                  const useLibrary = id === 'library';
                  const clearSuggestion = !useLibrary
                    && previous.purpose_source === 'suggested';
                  return {
                    ...previous,
                    use_library: useLibrary,
                    intent_course_name: useLibrary ? '' : previous.intent_course_name,
                    master_course_code: useLibrary ? previous.master_course_code : '',
                    preferred_language: useLibrary ? previous.preferred_language : '',
                    purpose_code: clearSuggestion ? '' : previous.purpose_code,
                    purpose_notes: clearSuggestion ? '' : previous.purpose_notes,
                    purpose_source: clearSuggestion ? '' : previous.purpose_source,
                  };
                })}
                options={COURSE_SOURCE_OPTIONS}
                variant="text"
                theme="employee"
                fullWidth
                ariaLabel="Course source"
              />
            </div>
            {form.use_library ? (
              <SearchableCombobox
                items={masterCourses || []}
                value={form.master_course_code}
                onChange={(value) => {
                  const course = (masterCourses || []).find((item) => item.code === value);
                  setForm((previous) => {
                    const canApplySuggestion = !previous.purpose_code
                      || previous.purpose_source === 'suggested';
                    const suggestion = course?.suggested_request_purpose_code || '';
                    const offersLanguage = courseOffersLanguagePreference(
                      value,
                      caps?.language_preference_course_codes,
                    );
                    return {
                      ...previous,
                      master_course_code: value,
                      preferred_language: offersLanguage ? previous.preferred_language : '',
                      purpose_code: canApplySuggestion && suggestion
                        ? suggestion
                        : previous.purpose_code,
                      purpose_notes: canApplySuggestion && suggestion
                        ? ''
                        : previous.purpose_notes,
                      purpose_source: canApplySuggestion && suggestion
                        ? 'suggested'
                        : previous.purpose_source,
                    };
                  });
                }}
                placeholder="Search course name or code…"
                searchPlaceholder="Type a course name or code…"
                emptyLabel="No matching courses"
                variant="form"
                theme="employee"
                displayFn={(course) => `${course.name} · ${course.code}`}
                valueFn={(course) => course.code}
                displayValue={selectedCourse
                  ? `${selectedCourse.name} · ${selectedCourse.code}`
                  : ''}
                searchFn={(course, term) => (
                  (course.name || '').toLowerCase().includes(term)
                  || (course.code || '').toLowerCase().includes(term)
                )}
                renderItem={({ item, isSelected, isHighlighted, onSelect }) => (
                  <button
                    key={item.code}
                    type="button"
                    onClick={onSelect}
                    className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50' : ''
                    } ${isHighlighted ? 'bg-gray-100' : ''}`}
                  >
                    <span className={`block truncate text-sm font-medium ${
                      isSelected ? 'text-blue-800' : 'text-gray-900'
                    }`}>
                      {item.name}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-gray-500">
                      {item.code}
                    </span>
                  </button>
                )}
              />
            ) : (
              <input
                required
                value={form.intent_course_name}
                onChange={(e) => setForm((p) => ({ ...p, intent_course_name: e.target.value }))}
                className="input w-full"
                placeholder="Course name for HR to book"
              />
            )}
            {showLanguagePicker && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Preferred language <span className="font-normal text-gray-400">optional</span>
                </label>
                <FormSelect
                  value={form.preferred_language}
                  onChange={(value) => setForm((p) => ({ ...p, preferred_language: value }))}
                  options={(caps?.language_choices || []).map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  placeholder="No preference"
                  theme="employee"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Providers often run this course in multiple languages. Let HR know if
                  {' '}
                  {caps?.can_nominate_workers && form.applicant_staff_id ? 'the worker' : 'you'}
                  {' '}
                  would be more comfortable attending in a specific language.
                </p>
              </div>
            )}
          </div>

          {/* 3. Why — consolidated legacy taxonomy; free text only for Other */}
          <div>
            <label className="form-label">
              Primary reason <span className="text-red-500">*</span>
            </label>
            <FormSelect
              value={form.purpose_code}
              onChange={(value) => setForm((p) => ({
                ...p,
                purpose_code: value,
                purpose_notes: value === COURSE_REQUEST_PURPOSE_OTHER
                  ? p.purpose_notes
                  : '',
                purpose_source: 'manual',
              }))}
              options={COURSE_REQUEST_PURPOSE_OPTIONS}
              placeholder="Select the primary reason"
              theme="employee"
              required
            />
            {form.purpose_source === 'suggested' && (
              <p className="mt-2 text-xs font-medium text-blue-800">
                Suggested from the selected course. Change it if this request has a different reason.
              </p>
            )}
            {form.purpose_code && (
              <p className="mt-1 text-xs text-gray-500">
                {COURSE_REQUEST_PURPOSE_OPTIONS.find((o) => o.value === form.purpose_code)?.description}
              </p>
            )}
            {form.purpose_code === COURSE_REQUEST_PURPOSE_OTHER && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Describe the purpose <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  minLength={10}
                  rows={3}
                  value={form.purpose_notes}
                  onChange={(e) => setForm((p) => ({ ...p, purpose_notes: e.target.value }))}
                  className="textarea w-full"
                  placeholder="Briefly explain why this training is needed (min. 10 characters)"
                />
              </div>
            )}
            {form.purpose_code === 'site_requirement' && !form.project_code && (
              <p className="mt-2 text-xs text-amber-700">
                Select the project below so HR can identify the requesting site.
              </p>
            )}
          </div>

          {/* 4. Where / project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Project <span className="font-normal text-gray-400">optional</span>
            </label>
            <SearchableCombobox
              items={activeProjects || []}
              value={form.project_code}
              onChange={(code) => {
                const project = (activeProjects || []).find((p) => p.project_code === code);
                setForm((p) => ({
                  ...p,
                  project_code: code || '',
                  project_site: project
                    ? formatProjectLabel(project.project_name, project.project_code)
                    : '',
                }));
              }}
              placeholder={projectsLoading ? 'Loading projects…' : 'Search project name or code…'}
              searchPlaceholder="Type to search…"
              emptyLabel="No matching projects"
              clearLabel="No project"
              loading={projectsLoading}
              variant="form"
              theme="employee"
              displayFn={(p) => formatProjectLabel(p.project_name, p.project_code)}
              valueFn={(p) => p.project_code}
              displayValue={projectDisplayValue}
              searchFn={(p, term) => {
                const q = term.toLowerCase();
                return (
                  (p.project_name || '').toLowerCase().includes(q)
                  || (p.project_code || '').toLowerCase().includes(q)
                  || (p.project_client_name || '').toLowerCase().includes(q)
                );
              }}
              renderItem={({ item, isSelected, isHighlighted, onSelect }) => (
                <button
                  key={item.project_code}
                  type="button"
                  onClick={onSelect}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                    isSelected ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-700'
                  } ${isHighlighted ? 'bg-gray-100' : ''}`}
                >
                  <span className="truncate">{item.project_name}</span>
                  <span className="text-xs text-gray-400 font-mono flex-shrink-0">{item.project_code}</span>
                </button>
              )}
            />
          </div>

          {/* Working-hours policy — courses are never off-hours / weekend-by-preference */}
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-blue-800" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-blue-900">
                  During working hours
                </h3>
                <p className="mt-1 text-sm text-blue-800 leading-relaxed">
                  No need to pick days or sessions — HR confirms the schedule after
                  approval. Plan for the attendee to be away from usual duties for the
                  course.
                </p>
              </div>
            </div>
          </div>

        </form>
      </Modal>

      <Modal
        isOpen={showSignAgreement}
        onClose={closeSignAgreement}
        title="Sponsorship Agreement (FHR009)"
        size="lg"
        theme="employee"
        footer={(
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeSignAgreement}
              className="btn btn-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sign-agreement-form"
              disabled={!declarationAccepted || !hasSignature || acknowledgeMutation.isPending}
              className="btn btn-primary inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <FileSignature className="w-4 h-4" />
              {acknowledgeMutation.isPending ? 'Signing…' : 'Sign & acknowledge'}
            </button>
          </div>
        )}
      >
        <form id="sign-agreement-form" onSubmit={handleAcknowledgeAgreement} className="space-y-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="field-label">Institution</dt>
                <dd className="field-display mt-0.5">{detail?.agreement_terms?.institution || '—'}</dd>
              </div>
              <div>
                <dt className="field-label">Course</dt>
                <dd className="field-display mt-0.5">
                  {detail?.agreement_terms?.course_name || detail?.display_course_name}
                </dd>
              </div>
              <div>
                <dt className="field-label">Sponsored course fee</dt>
                <dd className="field-display mt-0.5">
                  {formatCurrency(detail?.agreement_terms?.course_fee)}
                </dd>
              </div>
              <div>
                <dt className="field-label">Bond duration</dt>
                <dd className="field-display mt-0.5">
                  {detail?.agreement_terms?.bond_duration_months} months
                </dd>
              </div>
            </dl>
          </div>

          <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 p-4 text-sm text-gray-700 space-y-3">
            <p className="font-medium text-gray-900">
              In consideration of the Company&apos;s approval of my sponsorship application, I agree to:
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                Sponsorship is granted upon successful completion of the course. It excludes
                examination, registration, membership fees, and reference books, which I will
                bear myself.
              </li>
              <li>
                If I fail to complete or achieve certification of the course, I will retake and
                complete it at my own expense.
              </li>
              <li>
                I will continue to serve the Company for the bond duration above, starting from
                course completion. If I resign during that period, I will reimburse the Company a
                pro-rata amount of the sponsorship, plus 5% interest on the balance.
              </li>
              <li>
                If I resign during the course itself, I will repay the full sponsored amount plus
                an additional 5% interest.
              </li>
            </ol>
            <p className="text-xs text-gray-500">
              Full terms &amp; conditions are set out in the signed FHR009 PDF, which you can
              download from this page after signing.
            </p>
          </div>

          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              checked={declarationAccepted}
              onChange={(e) => setDeclarationAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-blue-700 text-blue-700 focus-visible:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              I declare that I have fully understood and agree with the above statements and the
              terms &amp; conditions of this Sponsorship Agreement, and confirm the information
              provided is true and correct.
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Your signature <span className="text-red-500">*</span>
            </label>
            <SignatureCanvas ref={signatureRef} onChange={setHasSignature} />
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={showCancelConfirm}
        theme="employee"
        variant="danger"
        title="Cancel this course request"
        confirmLabel="Cancel request"
        cancelLabel="Keep request"
        isLoading={decisionMutation.isPending}
        onCancel={() => {
          setShowCancelConfirm(false);
          setCancelReason('');
        }}
        onConfirm={async () => {
          if (!selectedId) return;
          await decisionMutation.mutateAsync({
            id: selectedId,
            action: 'cancel',
            comments: cancelReason,
          });
          setShowCancelConfirm(false);
          setCancelReason('');
        }}
      >
        <p className="text-sm text-gray-700">
          {detail?.status === 'booked'
            ? 'This withdraws the request. It does not cancel the training provider booking on its own.'
            : 'This withdraws the request before it is booked.'}
        </p>
        <textarea
          className="input w-full mt-3"
          rows={3}
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
          placeholder="Optional reason"
        />
      </ConfirmModal>
    </DashboardLayout>
  );
};

export default SelfServiceCourseRequests;
